import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { Boton } from './Boton';
import { Icono } from './Icono';
import type { NombreIcono } from './Icono';
import { IconoCaja } from './IconoCaja';
import { Importe } from './Importe';
import { color, num, texto } from './tokens';

/** Lo que se va a borrar, para que se reconozca de un vistazo. */
export type ContextoConfirmar = {
  /** Comercio o beneficiario («Mercadona»). */
  comercio?: string | null;
  /** Céntimos enteros. */
  importe?: number | null;
};

/** ¿Hay algo que enseñar en la línea de contexto? */
export function hayContexto(contexto?: ContextoConfirmar | null): boolean {
  return Boolean(
    contexto && (contexto.comercio || typeof contexto.importe === 'number'),
  );
}

type ConfirmarHojaProps = {
  icono: NombreIcono;
  /** La pregunta, en grande: «¿Borrar este movimiento?». */
  pregunta: ReactNode;
  /** Una línea con la consecuencia. */
  detalle?: ReactNode;
  contexto?: ContextoConfirmar | null;
  /** Texto del botón rojo («Borrar», «Desconectar», «Cerrar cuenta»). */
  textoAccion: ReactNode;
  onConfirmar?: () => void;
  onCancelar: () => void;
  /** `submit` cuando la hoja va dentro de un `<Form>`. */
  tipoAccion?: 'button' | 'submit';
  accionDeshabilitada?: boolean;
  testIdAccion?: string;
  /** Acción secundaria entre el botón rojo y Cancelar («Borrarla del todo»). */
  secundaria?: { texto: ReactNode; onPress: () => void; testId?: string };
  /** X redonda arriba a la derecha (cierra sin hacer nada). */
  onCerrar?: () => void;
  /** Lo que haga falta elegir antes de confirmar (la lista de destino). */
  children?: ReactNode;
};

/**
 * Confirmación de Rumbo (B6 · H73): icono rojo, la pregunta, una línea de
 * consecuencia, el contexto (comercio · importe), lo que haya que elegir y,
 * abajo, el botón de peligro rojo a todo el ancho con «Cancelar» debajo.
 * Es el contenido: va dentro de una `Hoja` o de un modal heredado en hoja.
 */
export function ConfirmarHoja({
  icono,
  pregunta,
  detalle,
  contexto,
  textoAccion,
  onConfirmar,
  onCancelar,
  tipoAccion = 'button',
  accionDeshabilitada,
  testIdAccion = 'hoja-accion',
  secundaria,
  onCerrar,
  children,
}: ConfirmarHojaProps) {
  const { t } = useTranslation();
  return (
    <View style={{ alignItems: 'stretch', gap: 0, position: 'relative' }}>
      {onCerrar && (
        <Button
          variant="bare"
          aria-label={t('Close')}
          data-testid="hoja-cerrar"
          onPress={onCerrar}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: color.surface2,
            color: color.fg,
            zIndex: 1,
          }}
        >
          <Icono nombre="x" size={16} />
        </Button>
      )}
      <View
        style={{ alignItems: 'center', textAlign: 'center', padding: '0 44px' }}
      >
        <IconoCaja icono={icono} tono="rojo" size={44} redonda />
        <Text
          role="heading"
          aria-level={2}
          style={{
            marginTop: 12,
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            color: color.fg,
            overflowWrap: 'anywhere',
          }}
        >
          {pregunta}
        </Text>
        {hayContexto(contexto) && (
          <View
            data-testid="confirmar-contexto"
            style={{
              marginTop: 10,
              flexDirection: 'row',
              alignItems: 'baseline',
              gap: 8,
              padding: '6px 12px',
              borderRadius: 999,
              backgroundColor: color.surface2,
              maxWidth: '100%',
            }}
          >
            {contexto?.comercio && (
              <Text
                style={{
                  fontWeight: 700,
                  color: color.fg,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {contexto.comercio}
              </Text>
            )}
            {typeof contexto?.importe === 'number' && (
              <Importe
                valor={contexto.importe}
                tono="ninguno"
                style={{ ...num, fontWeight: 800, color: color.fg }}
              />
            )}
          </View>
        )}
        {detalle && (
          <Text
            style={{
              ...texto.secundario,
              marginTop: 10,
              fontSize: 15,
              fontWeight: 500,
              lineHeight: 1.4,
              color: color.fg2,
            }}
          >
            {detalle}
          </Text>
        )}
      </View>
      {children && <View style={{ marginTop: 14 }}>{children}</View>}
      <View style={{ gap: 8, marginTop: 18, flexShrink: 0 }}>
        <Boton
          bloque
          variante="peligro"
          type={tipoAccion}
          isDisabled={accionDeshabilitada}
          data-testid={testIdAccion}
          onPress={onConfirmar}
        >
          {textoAccion}
        </Boton>
        {secundaria && (
          <Boton
            bloque
            variante="tonal"
            data-testid={secundaria.testId ?? 'hoja-secundaria'}
            onPress={secundaria.onPress}
          >
            {secundaria.texto}
          </Boton>
        )}
        <Boton
          bloque
          variante="fantasma"
          data-testid="hoja-cancelar"
          onPress={onCancelar}
        >
          <Trans>Cancel</Trans>
        </Boton>
      </View>
    </View>
  );
}

export type OpcionDestino = {
  id: string;
  nombre: string;
  /** Grupo o tipo, en pequeño a la derecha. */
  detalle?: string;
};

/**
 * Lista de destino dentro de la hoja (H55, H73): filas de 52 px con un
 * círculo de selección; si son muchas, la lista hace scroll por dentro y la
 * hoja no se corta.
 */
export function ListaDestinoHoja({
  etiqueta,
  opciones,
  elegida,
  onElegir,
  error,
  testId = 'lista-destino',
}: {
  etiqueta: ReactNode;
  opciones: OpcionDestino[];
  elegida?: string | null;
  onElegir: (id: string) => void;
  error?: ReactNode;
  testId?: string;
}) {
  return (
    <View>
      <Text
        style={{
          ...texto.etiqueta,
          color: color.fg3,
          margin: '0 0 6px 2px',
          display: 'block',
        }}
      >
        {etiqueta}
      </Text>
      <View
        role="radiogroup"
        data-testid={testId}
        style={{
          maxHeight: 'min(260px, 36vh)',
          overflowY: 'auto',
          borderRadius: 16,
          border: `1.5px solid ${error ? color.bad : color.line2}`,
        }}
      >
        {opciones.map((o, i) => {
          const activa = o.id === elegida;
          return (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={activa}
              data-testid={`${testId}-${o.id}`}
              onClick={() => onElegir(o.id)}
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                width: '100%',
                border: 0,
                cursor: 'pointer',
                font: 'inherit',
                minHeight: 52,
                padding: '0 14px',
                gap: 10,
                justifyContent: 'flex-start',
                textAlign: 'left',
                borderRadius: 0,
                borderTop: i === 0 ? 0 : `1px solid ${color.line}`,
                backgroundColor: activa ? color.accentSoft : 'transparent',
                color: color.fg,
                flexShrink: 0,
              }}
            >
              <View
                aria-hidden
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 11,
                  flexShrink: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: activa ? 0 : `2px solid ${color.fg4}`,
                  backgroundColor: activa ? color.accent : 'transparent',
                  color: color.accentInk,
                }}
              >
                {activa && <Icono nombre="check" size={14} />}
              </View>
              <Text
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 15,
                  fontWeight: activa ? 700 : 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {o.nombre}
              </Text>
              {o.detalle && (
                <Text style={{ fontSize: 12, color: color.fg3 }}>
                  {o.detalle}
                </Text>
              )}
            </button>
          );
        })}
      </View>
      {error && (
        <Text
          style={{ marginTop: 6, color: color.bad, fontSize: 13 }}
          role="alert"
        >
          {error}
        </Text>
      )}
    </View>
  );
}
